import { useCallback, useMemo } from "react";
import useAuthFetch from "../util/AuthFetch";

// Requests still in flight, keyed by the credentials in use plus the url. Overlapping callers
// of the same url share one request: unrelated components can ask for the same endpoint in the
// same render pass (e.g. InventorySelection's per-platform vehicle counts and
// InventoryContext's vehicle list, both triggered when a dealer lookup auto-selects a
// platform), and the backend cache only collapses requests that don't overlap in flight.
// Module scope on purpose -- every component calls useApi() separately, so a ref would give
// each its own map and no sharing at all. The auth key is part of the map key so that a
// request issued under a pre-refresh token is never handed to a caller that has already been
// given the rotated one, which would bypass the new credentials entirely.
const inFlight = new Map();

export default function useApi() {
  const authFetch = useAuthFetch();

  const simpleGetJson = useCallback((url, defaultValue = null) => {
    const cacheKey = `${authFetch.authKey}\n${url}`;
    const pending = inFlight.get(cacheKey);
    if (pending) {
      return pending;
    }

    const request = (async () => {
      let res = await authFetch(url);
      if (res.ok) {
        return res.json();
      } else if (res.status === 404) {
        return defaultValue;
      } else {
        // statusText is an empty string over HTTP/2, so lead with the status code. The body
        // carries the upstream status and message the Rust layer relayed.
        const body = await res.text().catch(() => "");
        console.error(`Error fetching ${url}: HTTP ${res.status} ${res.statusText}`.trim(), body);
        const error = new Error(`HTTP ${res.status} fetching ${url}${body ? `: ${body}` : ""}`);
        // Lets callers tell an expected failure (403 while a scope is still being granted)
        // apart from an unexpected one.
        error.status = res.status;
        throw error;
      }
    })().finally(() => inFlight.delete(cacheKey));

    inFlight.set(cacheKey, request);
    return request;
  }, [authFetch]);

  return useMemo(() => ({
    getUserInfo: () => simpleGetJson("/api/me"),

    /* Dealer Info */
    ewsLookup: (dealerId) => {
      console.log(`lookup ews ${dealerId}`);
      return simpleGetJson(`/api/ews-lookup/${dealerId}`);
    },
    corgLookup: (corgId) => {
      console.log(`lookup corg ${corgId}`);
      return simpleGetJson(`/api/corg-lookup/${corgId}`);
    },
    cviConfigLookup: (dealerId) => {
      console.log(`lookup cvi config ${dealerId}`);
      return simpleGetJson(`/api/cvi-config-lookup/${dealerId}`);
    },
    oemLookup: (oemId) => {
      console.log(`search for oem dealer ${oemId}`);
      return simpleGetJson(`/api/oem-lookup/${oemId}`);
    },
    getVirtualRooftopsForContact: (contactId) => {
      console.log("get virtual rooftops for contact " + contactId);
      return simpleGetJson(`/api/homenet-dealers/${contactId}`);
    },

    /* CVI */
    getCviVehiclesForEntity: (entityId, showSold = false) => {
      console.log("get vehicles from cvi for " + entityId);
      return simpleGetJson(`/api/cvi-vehicle-list/${entityId}?sold=${showSold}`, []);
    },
    getCviVehiclesCoOrg: async (corgId, filter) => {
      					let url;
					if (filter.trim()) {
						url = `/api/cvi-vehicle-list-corg-filter/${corgId}?filter=${encodeURIComponent(filter.trim())}`;
					} else {
						url = `/api/cvi-vehicle-list-corg/${corgId}`;
					}
          return simpleGetJson(url);
        },
    getCviVehiclesForVin: (vin) => {
      console.log("search vin in cvi for " + vin);
      return simpleGetJson(`/api/cvi-vin-search/${vin}`, []);
    },
    getCviSellableVehicle: (entityId, vehicleId) => {
      console.log(`get sellable vehicle from cvi for ${entityId} ${vehicleId}`);
      return simpleGetJson(`/api/cvi-vehicle/${entityId}/${vehicleId}`);
    },
    getCviVehicleChangeLog: (entityId, vehicleId) => {
      console.log(`get vehicle change log from cvi for ${entityId} ${vehicleId}`);
      return simpleGetJson(`/api/cvi-change-log/${entityId}/${vehicleId}`);
    },
    getCviVehicleLogEntries: (vehicleId) => {
      console.log(`get vehicle log entries from cvi for ${vehicleId}`);
      return simpleGetJson(`/api/cvi-vehicle-log-entries/${vehicleId}`);
    },
    getCviVehicleMedia: (vehicleId) => {
      console.log(`get vehicle media from cvi for ${vehicleId}`);
      return simpleGetJson(`/api/vehicle-media/${vehicleId}`, {});
    },
    getCviVehicleFromDiva: (entityId, vehicleId) => {
      console.log(`get vehicle from cvi diva for ${entityId} ${vehicleId}`);
      return simpleGetJson(`/api/diva-vehicle/${entityId}/${vehicleId}`);
    },
    /* CVI 2.1 (Inventory Records Service) */
    getCvi2VehiclesForDealer: (dealerId) => {
      console.log("get inventory records from cvi 2.1 for " + dealerId);
      return simpleGetJson(`/api/cvi2-vehicle-list/${dealerId}`, []);
    },
    getCvi2VehiclesForVin: (vin) => {
      console.log("search vin in cvi 2.1 for " + vin);
      return simpleGetJson(`/api/cvi2-vin-search/${vin}`, []);
    },
    getCvi2VehiclesForDealerVin: (dealerId, vin) => {
      console.log(`search vin in cvi 2.1 for ${dealerId} ${vin}`);
      return simpleGetJson(`/api/cvi2-vin-search/${dealerId}/${vin}`, []);
    },
    getCvi2InventoryRecord: (inventoryRecordId) => {
      console.log("get inventory record from cvi 2.1 for " + inventoryRecordId);
      return simpleGetJson(`/api/cvi2-inventory-record/${inventoryRecordId}`);
    },

    /**
     * The composed configuration keys a dealer is enabled for (BFF route `cvi2-composed-config`).
     * An empty `keys` is a normal answer -- the dealer is not enabled for composition -- and is
     * also what a 404 reads as, so callers never have to tell the two apart. The caller picks
     * which key to use (`keys[0]` today), in one named place.
     * @param {string} dealerId The Common Org id (`cvi2DealerId`).
     * @returns {Promise<{keys: string[]}>}
     */
    getCvi2ComposedConfig: (dealerId) => {
      console.log("get composed configuration from cvi 2 for " + dealerId);
      return simpleGetJson(`/api/cvi2-composed-config/${dealerId}`, { keys: [] });
    },
    /**
     * A dealer's records that have a composed vehicle under `key`, as the shared vehicle-list
     * rows (BFF route `cvi2-composed-list`). The row is the composed vehicle itself: there is
     * no "has no composed vehicle" row, so an empty array means the dealer has none.
     * @param {string} dealerId The Common Org id (`cvi2DealerId`).
     * @param {string} key A composed configuration key from `getCvi2ComposedConfig`. Percent-
     *   encoded here; the BFF encodes it again for the upstream query.
     * @returns {Promise<object[]>}
     */
    getCvi2ComposedVehiclesForDealer: (dealerId, key) => {
      console.log(`get composed vehicles from cvi 2 for ${dealerId} (${key})`);
      return simpleGetJson(`/api/cvi2-composed-list/${dealerId}/${encodeURIComponent(key)}`, []);
    },
    /**
     * One composed vehicle, unprojected, as the service returned it (BFF route
     * `cvi2-composed-vehicle`). `null` when the record has no composed vehicle under `key`
     * (a 404), which the detail view shows as no tab and no error.
     * @param {string} inventoryRecordId The inventory record id, a GUID.
     * @param {string} key A composed configuration key; percent-encoded here.
     * @returns {Promise<object|null>}
     */
    getCvi2ComposedVehicle: (inventoryRecordId, key) => {
      console.log(`get composed vehicle from cvi 2 for ${inventoryRecordId} (${key})`);
      return simpleGetJson(`/api/cvi2-composed-vehicle/${inventoryRecordId}/${encodeURIComponent(key)}`);
    },

    getVautoSourceVehiclesForEntity: (entityId) => {
      console.log("get vehicles from vauto source for " + entityId);
      return simpleGetJson(`/api/cvi-vauto-source-vehicles/${entityId}`, []);
    },

    /* Vauto */
    getVautoVehiclesForDealer: (vaDealerId, showInactive = false) => {
      console.log("get vehicles from vauto for " + vaDealerId);
      return simpleGetJson(`/api/vauto-vehicle-list/${vaDealerId}?inactive=${showInactive}`, []);
    },
    getVautoVehicleDetails: (id) => {
      console.log("get vauto vehicle details for " + id);
      return simpleGetJson(`/api/vauto-vehicle/${id}`);
    },
    getMvsVehiclesForEntity: (entityId) => {
      console.log("get vehicles from mvs for " + entityId);
      return simpleGetJson(`/api/mvs-vehicle-list/${entityId}`, []);
    },
    getMvsVehicleDetails: (id) => {
      console.log("get mvs vehicle details for " + id);
      return simpleGetJson(`/api/mvs-vehicle/${id}`);
    },

    /* IOL, DDC, VIN, ATC */
    getIolVehiclesForVrt: (vrtId) => {
      console.log("get vehicles from iol for " + vrtId);
      return simpleGetJson(`/api/iol-vehicle-list/${vrtId}`, []);
    },
    getIolVehicleDetails: (vrtId, id) => {
      console.log("get iol vehicle details for " + vrtId + " " + id);
      return simpleGetJson(`/api/iol-vehicle/${vrtId}/${id}`);
    },
    getDdcVehiclesForDealer: (ddcDealerId) => {
      console.log("get vehicles from ddc for " + ddcDealerId);
      return simpleGetJson(`/api/ddc-vehicle-list/${ddcDealerId}`, []);
    },
    getDdcVehicleDetails: (id) => {
      console.log("get ddc vehicle details for " + id);
      return simpleGetJson(`/api/ddc-vehicle/${id}`);
    },
    getVinVehiclesForDealer: (vinDealerId) => {
      console.log("get vehicles from vin for " + vinDealerId);
      return simpleGetJson(`/api/vin-vehicle-list/${vinDealerId}`, []);
    },
    getAtcVehiclesForDealer: (atcDealerId) => {
      console.log("get vehicles from atc for " + atcDealerId);
      return simpleGetJson(`/api/atc-vehicle-list/${atcDealerId}`, []);
    },
    getAtcVehicleDetails: (id) => {
      console.log("get atc vehicle details for " + id);
      return simpleGetJson(`/api/atc-vehicle/${id}`);
    },

    /* Events */
    getEventsList: () => {
      console.log("get events list");
      return simpleGetJson("/api/events", []);
    },
    getEventDetails: (filename) => {
      console.log("get event details for " + filename);
      return simpleGetJson(`/api/events/${filename}`);
    },
  }), [simpleGetJson]);
}
